import React, { useEffect, useRef } from 'react';
import { THREAD_PATH, THREAD_VIEWBOX } from '../data/threadPath';

/**
 * A length of thread that stitches itself across the page as you scroll,
 * with a needle riding the end of it.
 *
 * Adapted from the "stroke follows scroll progress" pattern (Skiper UI 19),
 * with three deliberate departures:
 *
 *  - No framer-motion. The whole effect is one number — how far through the
 *    viewport the band has travelled — mapped onto `stroke-dashoffset`. That
 *    is twenty lines of plain JS, not a 40 KB dependency.
 *  - No 350vh section. The original is a demo page; here the thread draws
 *    over the ordinary scroll distance of a band between two sections.
 *  - A thread, not a squiggle. Same mechanism, drawn in the palette, because
 *    an Aari atelier has a better claim to this motif than most.
 *
 * `pathLength="1"` normalises the path so dasharray/dashoffset work in the
 * range 0..1 regardless of the path's real length — no getTotalLength(), no
 * measurement on resize.
 *
 * Motion rules: the needle moves by transform only. The thread itself is a
 * stroke-dashoffset change, which does repaint — but it is one path, updated
 * at most once per frame, and only while the band is on screen. Both stop
 * entirely under prefers-reduced-motion, where the thread renders fully
 * stitched and the needle rests at its end.
 */

// Progress runs 0 → 1 as the band's top travels from the bottom of the
// viewport to a quarter of the way down it, so the stitch completes while
// the band is still comfortably in view rather than as it leaves.
const START = 1.0;
const END = 0.25;

export default function ThreadDivider() {
  const svgRef = useRef(null);
  const pathRef = useRef(null);
  const needleRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    const path = pathRef.current;
    const needle = needleRef.current;
    if (!svg || !path || !needle) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Real length, used only to place the needle along the curve. The dash
    // maths uses the normalised pathLength="1" instead.
    const total = path.getTotalLength();

    const apply = (p) => {
      path.style.strokeDashoffset = String(1 - p);

      // Needle sits at the drawn end, pointing along the tangent.
      const at = Math.max(total * p, 0.01);
      const tip = path.getPointAtLength(at);
      const back = path.getPointAtLength(Math.max(at - 3, 0));
      const angle = (Math.atan2(tip.y - back.y, tip.x - back.x) * 180) / Math.PI;
      needle.style.transform = `translate(${tip.x}px, ${tip.y}px) rotate(${angle}deg)`;
      needle.style.opacity = p > 0.995 ? '0' : '1';
    };

    if (reduce) {
      apply(1);
      return undefined;
    }

    let raf = 0;
    let visible = false;

    const measure = () => {
      raf = 0;
      const rect = svg.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const p = (vh * START - rect.top) / (vh * (START - END));
      apply(Math.min(Math.max(p, 0), 1));
    };

    const onScroll = () => {
      if (!visible || raf) return;
      raf = requestAnimationFrame(measure);
    };

    // Only listen while the band is anywhere near the viewport. A scroll
    // handler that runs for the whole page to update something off-screen is
    // how a decorative flourish becomes the most expensive thing on the site.
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) measure();
      },
      { rootMargin: '25% 0px 25% 0px' }
    );
    io.observe(svg);

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative w-full h-36 sm:h-44 overflow-hidden pointer-events-none select-none"
    >
      {/* `slice` keeps the loops at their drawn size on every screen and
          crops the sides on phones, so the thread enters from off-screen
          left instead of being squashed to fit. */}
      <svg
        ref={svgRef}
        viewBox={THREAD_VIEWBOX}
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        fill="none"
      >
        {/* Faint guide of the full stitch, so the eye knows where the thread
            is heading before it gets there. */}
        <path
          d={THREAD_PATH}
          stroke="#4E2C23"
          strokeOpacity="0.08"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          ref={pathRef}
          d={THREAD_PATH}
          pathLength="1"
          stroke="#E2725B"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="1"
          strokeDashoffset="1"
        />
        {/* Needle: a short shaft with an eye at the trailing end. Positioned
            by transform from the effect; its own origin is the tip. */}
        <g ref={needleRef} style={{ transformOrigin: '0 0', willChange: 'transform' }}>
          <line x1="0" y1="0" x2="-30" y2="0" stroke="#4E2C23" strokeWidth="2.5" strokeLinecap="round" />
          <ellipse cx="-24" cy="0" rx="3.2" ry="1.8" fill="#FAF2EB" stroke="#4E2C23" strokeWidth="1.4" />
        </g>
      </svg>
    </div>
  );
}
